import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { BUSINESS } from "../../lib/constants";
import { CountUp } from "./CountUp";

const HERO_IMAGE = "https://images.unsplash.com/photo-1523559094051-53bac879eb80?crop=entropy&cs=srgb&fm=jpg&q=85";

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.85, delay: 0.18 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

function MaskedLine({ text, className, i }) {
  return (
    <span className="block overflow-hidden py-1">
      <motion.span custom={i} initial="hidden" animate="visible" variants={lineVariants} className={`block ${className}`}>
        {text}
      </motion.span>
    </span>
  );
}

export function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.8, 0.95]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden bg-obsidian" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img src={HERO_IMAGE} alt="Heavy diesel engine" className="w-full h-full object-cover opacity-35" />
      </motion.div>
      <div className="absolute inset-0 bg-obsidian/70" />
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/95 to-obsidian/75" />
      <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-obsidian/80" />
      <div className="absolute inset-0 bg-noise" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-24 pb-16">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="inline-flex items-center gap-2 border border-amber-500/30 rounded-full px-4 py-1.5 mb-6 bg-black/40"
            >
              <ShieldCheck className="h-3.5 w-3.5 text-amber-light" />
              <span className="text-xs font-mono uppercase tracking-[0.25em] text-amber-light">OEM-Grade Heavy Duty Components</span>
            </motion.div>

            <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl uppercase leading-[0.95] text-white">
              <MaskedLine text="FORGED FOR" i={0} />
              <MaskedLine text="THE HEAVY" i={1} />
              <MaskedLine text="HAUL." className="text-gradient-brass" i={2} />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.6 }}
              className="mt-6 max-w-xl text-base sm:text-lg text-zinc-300 leading-relaxed"
            >
              {BUSINESS.name} supplies precision engineered automotive, truck & tractor parts — engines to hydraulics — with transparent SKUs and rapid quote turnaround.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.6 }}
              className="mt-9 flex flex-wrap gap-4"
            >
              <Link
                to="/catalog"
                data-testid="hero-cta-catalog-button"
                className="inline-flex items-center gap-2 bg-amber-light text-obsidian font-semibold px-7 py-3.5 rounded-sm uppercase text-sm tracking-wide hover:bg-white transition-colors duration-300"
              >
                Browse Catalog <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/quote"
                data-testid="hero-cta-quote-button"
                className="inline-flex items-center gap-2 border border-amber-500/40 text-white font-semibold px-7 py-3.5 rounded-sm uppercase text-sm tracking-wide hover:bg-amber-500/10 hover:border-amber-500/70 transition-colors duration-300"
              >
                Request a Quote
              </Link>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.7 }}
          className="mt-20 grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-amber-500/15 pt-8"
        >
          {[
            { target: 500, suffix: "+", label: "SKUs Available" },
            { target: 8, suffix: "", label: "Part Categories" },
            { target: 24, suffix: "HR", label: "Quote Turnaround" },
            { target: 15, suffix: "+ YR", label: "Combined Field Experience" },
          ].map((s) => (
            <div key={s.label}>
              <div className="font-display text-3xl md:text-4xl text-amber-light">
                <CountUp target={s.target} suffix={s.suffix} />
              </div>
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mt-1">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
